import { Container, IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { BaseView } from "../../../../../../scene/viewable/BaseView";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { Web3Model } from "../../../../../../web3/models/Web3Model";
import { ILogger } from "../../../../../logger/ILogger";
import { IFocusable } from "../../../../layers/IFocusable";
import { FullscreenInputBlocker } from "../../../../game/components/FullscreenInputBlocker";
import { nameof } from "../../../../../../shared/bindable-data";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { ISignMessageController } from "../controllers/ISignMessageController";
import { GameFont } from "../../../../common/GameFont";
import { ButtonWithScaleAndPosYAnimation } from "../../../../common/components/ButtonWithScaleAndPosYAnimation";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import gsap from "gsap";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { SignMessageTextContainer } from "../components/SignMessageTextContainer";
import { SignMessageOptions } from "../SignMessageScene";
import { SignMessageResourcePackage } from "../resources/SignMessageResourcePackage";
type Timeline = gsap.core.Timeline;

export class SignMessageView extends BaseView<string> implements IFocusable {
    private static readonly HEADER_TEXT = 'SIGN MESSAGE';

    public readonly focusable: boolean = true;

    private readonly commonResources: CommonResourcesPackage;
    private readonly resourcePackage: SignMessageResourcePackage;
    private readonly signMessageController: ISignMessageController;
    private readonly web3Model: Web3Model;
    private readonly options: SignMessageOptions;
    private readonly canvasScaler: ICanvasScaler;
    private readonly logger: ILogger;

    private fade: FadeScreen;
    private leftLight: Sprite;
    private rightLight: Sprite;
    private headerIcon: Sprite;
    private headerText: Text;
    private frame: Container;
    private signButton: ButtonWithScaleAndPosYAnimation;
    private textContainer: SignMessageTextContainer;
    private disconnectButton: ButtonWithScaleAndPosYAnimation;
    private closeButton: ButtonWithScaleAnimation;
    private showAnimationTimeline: Timeline;

    public constructor(
        commonResources: CommonResourcesPackage,
        resourcePackage: SignMessageResourcePackage,
        signMessageController: ISignMessageController,
        web3Model: Web3Model,
        options: SignMessageOptions,
        canvasScaler: ICanvasScaler,
        logger: ILogger,
        operationControls: PromiseCompletionSource<string>
    ) {
        super(operationControls);
        this.commonResources = commonResources;
        this.resourcePackage = resourcePackage;
        this.signMessageController = signMessageController;
        this.web3Model = web3Model;
        this.options = options;
        this.canvasScaler = canvasScaler;
        this.logger = logger;
        this.build();
        this.layout(canvasScaler.referenceWidth, canvasScaler.referenceHeight);
        this.web3Model.propertyChanged.add(this.onWeb3ModelPropertyChanged, this);
    }

    public start(): void {
        if (!this.web3Model.connected.value) {
            this.operationControls.complete(null);
        }
        this.show();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.web3Model.propertyChanged.remove(this.onWeb3ModelPropertyChanged, this);
        this.closeButton.onUp.remove(this.onDisconnectButtonClick, this);
        this.signButton.onUp.remove(this.onSignButtonClick, this);
        this.disconnectButton.onUp.remove(this.onDisconnectButtonClick, this);
        this.showAnimationTimeline?.kill();
        super.destroy(options);
    }

    private async onWeb3ModelPropertyChanged(model: Web3Model, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "connected"):
                if (!model.connected.value) {
                    this.logger.debug(`[SignMessageView] Model not connected, close`);
                    await this.hide();
                    this.operationControls.complete(null);
                }
                break;
        }
    }

    private build(): void {
        this.fade = new FadeScreen(0.5, 0, 0.6, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fade);

        this.leftLight = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.rightLight = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.rightLight.scale.x = -1;
        this.addChild(this.leftLight, this.rightLight);

        this.headerIcon = new Sprite(this.resourcePackage.assets_views_sign_message_headerIcon_png);
        this.headerText = new Text(SignMessageView.HEADER_TEXT, new HeaderTextStyle());
        this.headerText.skew.x = -0.08;
        this.headerText.angle = -5;
        this.addChild(this.headerIcon, this.headerText);

        this.frame = new Container();
        const frameBackground = new Sprite(this.resourcePackage.assets_views_sign_message_frameBG_png);
        frameBackground.alpha = 0.6;
        frameBackground.tint = "b8b8b8";

        this.textContainer = new SignMessageTextContainer();

        this.signButton = new ButtonWithScaleAndPosYAnimation(this.commonResources.assets_common_ui_button_green_png);
        this.signButton.width = 305.42;
        this.signButton.height = 156.46;
        this.signButton.position.set(42, 458);
        this.signButton.onUp.add(this.onSignButtonClick, this);

        const signButtonText = new Text("SIGN MESSAGE", new ButtonTextStyle());
        this.signButton.addChild(signButtonText);
        signButtonText.position.set((this.signButton.defaultView.width - signButtonText.width) * 0.5, (this.signButton.defaultView.height - signButtonText.height) * 0.5);

        this.disconnectButton = new ButtonWithScaleAndPosYAnimation(this.commonResources.assets_common_ui_button_orange_png);
        this.disconnectButton.width = 296.42;
        this.disconnectButton.height = 147.46;
        this.disconnectButton.position.set(372, 457);
        this.disconnectButton.onUp.add(this.onDisconnectButtonClick, this);
        const disconnectButtonText = new Text("CANCEL", new ButtonTextStyle());
        this.disconnectButton.addChild(disconnectButtonText);
        disconnectButtonText.position.set((this.disconnectButton.defaultView.width - disconnectButtonText.width) * 0.5, (this.disconnectButton.defaultView.height - disconnectButtonText.height) * 0.5);

        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.defaultView.width = this.closeButton.defaultView.height = 76;
        this.closeButton.onUp.add(this.onDisconnectButtonClick, this);
        this.addChild(this.closeButton);

        this.frame.addChild(
            frameBackground,
            this.textContainer,
            this.signButton,
            this.disconnectButton
        );
        this.addChild(this.frame);
    }

    private async onSignButtonClick(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        const signature = await this.signMessageController.signMessage(this.options.walletType, this.options.address);
        if (signature != null || signature === "") {
            await this.hide();
            this.operationControls.complete(signature);
        }
        this.removeChild(inputBlocker);
    }

    private async onDisconnectButtonClick(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.hide();
        this.operationControls.complete(null);
        this.removeChild(inputBlocker);
    }

    private layout(width: number, _height: number): void {
        this.leftLight.position.set(0, 873);
        this.rightLight.position.set(width, 873);
        this.headerIcon.position.set((width - this.headerIcon.width) * 0.5, 150);
        this.headerText.position.set((width - this.headerText.width) * 0.5, this.headerIcon.position.y + this.headerIcon.height + 40);
        this.frame.position.set((width - this.frame.width) * 0.5, this.headerText.position.y + this.headerText.height - 44);
        this.textContainer.position.set(109, 211);
        this.closeButton.position.set((width - this.closeButton.width) * 0.5, this.frame.position.y + this.frame.height + 73);
    }

    private async show(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.fade.fadeIn();
        this.showAnimationTimeline = new gsap.core.Timeline({ paused: true });
        const headerInitialPosition = this.headerIcon.position.clone();
        this.showAnimationTimeline.fromTo(this.headerIcon.position,
            { x: headerInitialPosition.x + this.headerIcon.width * 0.5, y: headerInitialPosition.y + this.headerIcon.height * 0.5 },
            { x: headerInitialPosition.x, y: headerInitialPosition.y }, "<"
        );
        this.showAnimationTimeline.fromTo(this.headerIcon.scale, { x: 0, y: 0 }, { x: 1, y: 1 }, "<");

        this.showAnimationTimeline.fromTo(this.headerText, { alpha: 0 }, { alpha: 1, duration: 0.3 }, "<");

        const frameInitialPosition = this.frame.position.clone();
        this.showAnimationTimeline.fromTo(this.frame.position,
            { x: frameInitialPosition.x + this.frame.width * 0.5, y: frameInitialPosition.y + this.frame.height * 0.5 },
            { x: frameInitialPosition.x, y: frameInitialPosition.y }, "<"
        );
        this.showAnimationTimeline.fromTo(this.frame.scale, { x: 0, y: 0 }, { x: 1, y: 1 }, "<");

        this.showAnimationTimeline.fromTo(this.closeButton, { alpha: 0 }, { alpha: 1 }, "<");

        await this.showAnimationTimeline.play();
        this.removeChild(inputBlocker);
    }

    private async hide(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.fade.fadeOut();
        await this.showAnimationTimeline?.reverse();
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super({
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 130,
            fill: [
                "#AAFD09",
                "#16CF15"
            ],
            fillGradientType: 0,
            lineHeight: 102,
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowAngle: 0.5,
            dropShadowColor: "#0d0606"
        })
    }
}

class MessageTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#FFFFFF",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 46,
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowAngle: 0.5,
            dropShadowColor: "#0d0606"
        });
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            dropShadow: true,
            dropShadowAlpha: 0.5,
            dropShadowAngle: 0.5,
            dropShadowBlur: 5,
            dropShadowDistance: 4,
            fontSize: 57,
            leading: 15
        });
    }
}